import React, { useState, useEffect } from "react";
import supabase from "../../../supabaseClient";
import {
	Button,
	Modal,
	Box,
	Typography,
	CircularProgress,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	TextField,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@mui/material";
import { formatEggQuantity } from "../../../utils/eggQuantity";

const WHOLESALE_PAYMENT_TERMS_DAYS = 30;

// Retail pricing
// eslint-disable-next-line no-unused-vars
const RETAIL_CHICKEN_EGG_PRICE_PER_DOZEN = 5.0; // $5.00/dozen
// eslint-disable-next-line no-unused-vars
const RETAIL_DUCK_EGG_PRICE_PER_HALF_DOZEN = 4.0; // $4.00/half-dozen
// eslint-disable-next-line no-unused-vars
const RETAIL_DUCK_EGG_PRICE_PER_DOZEN = 8.0; // $8.00/dozen

// Wholesale pricing (invoiced per dozen, no duck half-dozens)
const WHOLESALE_CHICKEN_EGG_PRICE_PER_DOZEN = 4.0; // $4.00/dozen
const WHOLESALE_DUCK_EGG_PRICE_PER_DOZEN = 7.0; // $7.00/dozen

const AdminEggList = () => {
	const [eggList, setEggList] = useState([]);
	const [show401, setShow401] = useState(false);
	const [error, setError] = useState(null);
	const [status, setStatus] = useState("loading");
	const [markingAsReady, setMarkingAsReady] = useState(false);
	const [showActive, setShowActive] = useState("pending");
	const [fulfillmentNote, setFulfillmentNote] = useState("");
	const [eggRequests, setEggRequests] = useState([]);
	const [markReadyModal, setMarkReadyModal] = useState({
		open: false,
		customer: null,
		request: null,
	});
	const [markAsPickedUpModal, setMarkAsPickedUpModal] = useState({
		open: false,
		customer: null,
		request: null,
	});

	const [cancelModal, setCancelModal] = useState({
		open: false,
		customer: null,
		request: null,
		reason: "",
		loading: false,
	});

	const [remindModal, setRemindModal] = useState({
		open: false,
		request: null,
		customer: null,
		loading: false,
	});

	const [delayModal, setDelayModal] = useState({
		open: false,
		customer: null,
		request: null,
		note: "",
		loading: false,
	});

	const [acknowledgeModal, setAcknowledgeModal] = useState({
		open: false,
		customer: null,
		request: null,
		estimatedDeliveryDate: "",
		fulfillmentNote: "",
		loading: false,
	});

	const [createInvoiceDialog, setCreateInvoiceDialog] = useState({
		open: false,
		customer: null,
		request: null,
		estimatedDeliveryDate: "",
	});

	const [invoiceForm, setInvoiceForm] = useState(null);
	const [invoiceEstimatedDeliveryDate, setInvoiceEstimatedDeliveryDate] =
		useState("");
	const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
	const [invoiceSaveStatus, setInvoiceSaveStatus] = useState("");
	const [invoiceSaving, setInvoiceSaving] = useState(false);

	const [markAsDeliveredModal, setMarkAsDeliveredModal] = useState({
		open: false,
		customer: null,
		request: null,
		loading: false,
	});

	useEffect(() => {
		const fetchEggList = async () => {
			setStatus("loading");
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				setShow401(true);
				setStatus("error");
				return;
			}
			const { data: eggData, error: eggError } = await supabase
				.from("egg_list")
				.select("*")
				.order("created_at", { ascending: false });

			if (eggError) {
				setError(eggError.message);
				setStatus("error");
				return;
			}

			const { data: eggRequests, error: eggRequestsError } = await supabase
				.from("egg_requests")
				.select("*")
				.order("requested_at", { ascending: false });

			if (eggRequestsError) {
				setError(eggRequestsError.message);
				setStatus("error");
				return;
			}

			setEggRequests(eggRequests);

			// Attach active request to each customer
			const eggListWithRequests = eggData.map((customer) => {
				const anyRequest = eggRequests.find(
					(request) => request.customer_id === customer.supabaseId,
				);

				return {
					...customer,
					anyRequest,
				};
			});

			setEggList(eggListWithRequests);
			setStatus("loaded");
		};
		fetchEggList();
	}, []);

	const handleToggle = (_, value) => {
		if (value) setShowActive(value);
	};

	const getUserStats = (customer) => {
		const userRequests = eggRequests.filter(
			(r) => r.customer_id === customer.supabaseId,
		);

		const completed = userRequests.filter(
			(r) => r.request_status === "completed",
		).length;
		const pending = userRequests.filter(
			(r) => r.request_status === "pending",
		).length;
		const cancelled = userRequests.filter(
			(r) => r.request_status === "cancelled",
		).length;

		return { completed, pending, cancelled };
	};

	const handleOpenMarkReady = (customer, request) => {
		setMarkReadyModal({ open: true, customer, request });
		setFulfillmentNote("");
	};

	const handleCloseMarkReady = () => {
		setMarkReadyModal({ open: false, customer: null, request: null });
	};

	const handleOpenMarkAsPickedUp = (customer, request) => {
		setMarkAsPickedUpModal({ open: true, customer, request });
	};

	const handleCloseMarkAsPickedUp = () => {
		setMarkAsPickedUpModal({ open: false, customer: null, request: null });
	};

	const handleOpenRemindModal = (customer, request) => {
		setRemindModal({ open: true, customer, request, loading: false });
	};

	const handleCloseRemindModal = () => {
		setRemindModal({
			open: false,
			customer: null,
			request: null,
			loading: false,
		});
	};

	const handleConfirmRemind = async () => {
		setRemindModal((prev) => ({ ...prev, loading: true }));
		await handleRemindUser(remindModal.request.id, remindModal.request);
		setRemindModal({
			open: false,
			customer: null,
			request: null,
			loading: false,
		});
	};

	const handleOpenCancelModal = (customer, request) => {
		setCancelModal({
			open: true,
			customer,
			request,
			reason: "",
			loading: false,
		});
	};

	const handleCloseCancelModal = () => {
		setCancelModal({
			open: false,
			customer: null,
			request: null,
			reason: "",
			loading: false,
		});
	};

	const handleOpenDelayModal = (customer, request) => {
		setDelayModal({ open: true, customer, request, note: "", loading: false });
	};

	const handleCloseDelayModal = () => {
		setDelayModal({
			open: false,
			customer: null,
			request: null,
			note: "",
			loading: false,
		});
	};

	const handleSendDelay = async () => {
		setDelayModal((prev) => ({ ...prev, loading: true }));
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setShow401(true);
			setDelayModal((prev) => ({ ...prev, loading: false }));
			return;
		}
		const response = await fetch(
			`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delay-egg-order`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${session.access_token}`,
				},
				body: JSON.stringify({
					request_id: delayModal.request.id,
					delay_note: delayModal.note,
				}),
			},
		);

		if (response.ok) {
			// Optionally update UI to reflect delay sent
			delayModal.request.delay_notification_sent = true;
			handleCloseDelayModal();
			// Show a snackbar or alert if you wish
		} else {
			// Show error
			setDelayModal((prev) => ({ ...prev, loading: false }));
		}
	};

	const handleCancelOrder = async () => {
		setCancelModal((prev) => ({ ...prev, loading: true }));
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setShow401(true);
			setCancelModal((prev) => ({ ...prev, loading: false }));
			return;
		}
		const response = await fetch(
			`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/cancel-egg-order`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${session.access_token}`,
				},
				body: JSON.stringify({
					request_id: cancelModal.request.id,
					cancellation_reason: cancelModal.reason,
				}),
			},
		);
		const result = await response.json();
		if (response.ok) {
			// Refresh the list or update UI as needed
			const updatedEggList = eggList.map((customer) => {
				if (
					customer.anyRequest &&
					customer.anyRequest.id === cancelModal.request.id
				) {
					return {
						...customer,
						anyRequest: {
							...customer.anyRequest,
							request_status: "cancelled",
							cancellation_reason: cancelModal.reason,
						},
					};
				}
				return customer;
			});
			setEggList(updatedEggList);
			handleCloseCancelModal();
			alert("Order cancelled and customer notified.");
		} else {
			setCancelModal((prev) => ({ ...prev, loading: false }));
			setError(result.error || "Failed to cancel order.");
		}
	};

	const handleMarkReady = async (requestId) => {
		try {
			setMarkingAsReady(true);
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				setShow401(true);
				return;
			} else {
				setShow401(false);
			}

			const response = await fetch(
				`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/mark-egg-request-ready`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
					body: JSON.stringify({
						request_id: requestId,
						fulfillment_note: fulfillmentNote,
					}),
				},
			);
			const result = await response.json();
			if (response.ok) {
				// Refresh the list
				const { data: eggData, error: eggError } = await supabase
					.from("egg_list")
					.select("*")
					.order("created_at", { ascending: false });
				if (eggError) {
					setError(eggError.message);
					setStatus("error");
					return;
				}
				const { data: eggRequests, error: eggRequestsError } = await supabase
					.from("egg_requests")
					.select("*")
					.order("requested_at", { ascending: false });
				if (eggRequestsError) {
					setError(eggRequestsError.message);
					setStatus("error");
					return;
				}
				// Attach active request to each customer
				const eggListWithRequests = eggData.map((customer) => {
					const anyRequest = eggRequests.find(
						(request) => request.customer_id === customer.supabaseId,
					);
					return {
						...customer,
						anyRequest,
					};
				});

				setEggList(eggListWithRequests);
				setMarkingAsReady(false);
			} else {
				setMarkingAsReady(false);
				setError(result.error || "Failed to mark request as ready.");
			}
		} catch (err) {
			setMarkingAsReady(false);
			setError(err.message);
		}

		setMarkReadyModal({ open: false, customer: null, request: null });
	};

	const handleRemindUser = async (requestId, anyRequest) => {
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setShow401(true);
			return;
		} else {
			setShow401(false);
		}
		const response = await fetch(
			`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/remind-egg-order`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${session.access_token}`,
				},
				body: JSON.stringify({
					request_id: requestId,
				}),
			},
		);
		const result = await response.json();
		if (response.ok) {
			// Refresh the list
			const updatedEggList = eggList.map((customer) => {
				if (customer.anyRequest && customer.anyRequest.id === requestId) {
					return {
						...customer,
						anyRequest: {
							...customer.anyRequest,
							has_been_reminded: true,
						},
					};
				}
				return customer;
			});
			setEggList(updatedEggList);
			alert(`Reminder sent to egg request customer successfully.`);
		} else {
			setError(result.error || "Failed to send reminder.");
		}
	};

	const handleToggleElevatedOrdering = async (customer) => {
		const newValue = !customer.elevated_ordering;
		const { error } = await supabase
			.from("egg_list")
			.update({ elevated_ordering: newValue })
			.eq("id", customer.id);
		if (error) {
			setError(error.message);
			return;
		}
		setEggList((prev) =>
			prev.map((c) =>
				c.id === customer.id ? { ...c, elevated_ordering: newValue } : c,
			),
		);
	};

	// ── Acknowledge Wholesale Order ──────────────────────────────────────────

	const handleOpenAcknowledgeModal = (customer, request) => {
		setAcknowledgeModal({
			open: true,
			customer,
			request,
			estimatedDeliveryDate: "",
			fulfillmentNote: "",
			loading: false,
		});
	};

	const handleCloseAcknowledgeModal = () => {
		setAcknowledgeModal({
			open: false,
			customer: null,
			request: null,
			estimatedDeliveryDate: "",
			fulfillmentNote: "",
			loading: false,
		});
	};

	const refreshEggList = async () => {
		const { data: eggData, error: eggError } = await supabase
			.from("egg_list")
			.select("*")
			.order("created_at", { ascending: false });
		if (eggError) {
			setError(eggError.message);
			setStatus("error");
			return;
		}
		const { data: freshRequests, error: requestsError } = await supabase
			.from("egg_requests")
			.select("*")
			.order("requested_at", { ascending: false });
		if (requestsError) {
			setError(requestsError.message);
			setStatus("error");
			return;
		}
		setEggRequests(freshRequests);
		setEggList(
			eggData.map((customer) => ({
				...customer,
				anyRequest: freshRequests.find(
					(r) => r.customer_id === customer.supabaseId,
				),
			})),
		);
	};

	const handleAcknowledgeWholesaleOrder = async () => {
		setAcknowledgeModal((prev) => ({ ...prev, loading: true }));
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setShow401(true);
			setAcknowledgeModal((prev) => ({ ...prev, loading: false }));
			return;
		}

		const response = await fetch(
			`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/acknowledge-wholesale-egg-order`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${session.access_token}`,
				},
				body: JSON.stringify({
					request_id: acknowledgeModal.request.id,
					estimated_delivery_date: acknowledgeModal.estimatedDeliveryDate,
					fulfillment_note: acknowledgeModal.fulfillmentNote,
				}),
			},
		);

		if (response.ok) {
			const savedCustomer = acknowledgeModal.customer;
			const savedRequest = acknowledgeModal.request;
			const savedDeliveryDate = acknowledgeModal.estimatedDeliveryDate;
			handleCloseAcknowledgeModal();
			await refreshEggList();
			setCreateInvoiceDialog({
				open: true,
				customer: savedCustomer,
				request: savedRequest,
				estimatedDeliveryDate: savedDeliveryDate,
			});
		} else {
			setAcknowledgeModal((prev) => ({ ...prev, loading: false }));
		}
	};

	// ── Create Invoice from Order ────────────────────────────────────────────

	const handleOpenInvoiceFromOrder = () => {
		const { customer, request, estimatedDeliveryDate } = createInvoiceDialog;

		const deliveryDate = new Date(estimatedDeliveryDate + "T00:00:00");
		const dueDate = new Date(deliveryDate);
		dueDate.setDate(dueDate.getDate() + WHOLESALE_PAYMENT_TERMS_DAYS);

		const lineItems = [];
		if (request.chicken_eggs_requested > 0) {
			const dozens = request.chicken_eggs_requested / 12;
			lineItems.push({
				description: `Chicken Eggs (${formatEggQuantity(request.chicken_eggs_requested)} @ $${WHOLESALE_CHICKEN_EGG_PRICE_PER_DOZEN.toFixed(2)}/dozen)`,
				quantity: dozens,
				price: WHOLESALE_CHICKEN_EGG_PRICE_PER_DOZEN,
			});
		}
		if (request.duck_eggs_requested > 0) {
			const dozens = request.duck_eggs_requested / 12;
			lineItems.push({
				description: `Duck Eggs (${formatEggQuantity(request.duck_eggs_requested)} @ $${WHOLESALE_DUCK_EGG_PRICE_PER_DOZEN.toFixed(2)}/dozen)`,
				quantity: dozens,
				price: WHOLESALE_DUCK_EGG_PRICE_PER_DOZEN,
			});
		}
		if (lineItems.length === 0) {
			lineItems.push({ description: "", quantity: 1, price: 0 });
		}

		setInvoiceForm({
			customer_name: `${customer.first_name} ${customer.last_name}`,
			customer_email: customer.email || "",
			phone_number: "",
			due_date: dueDate.toISOString().slice(0, 10),
			line_items: lineItems,
			total: lineItems.reduce(
				(sum, item) => sum + item.quantity * item.price,
				0,
			),
		});
		setInvoiceEstimatedDeliveryDate(estimatedDeliveryDate || "");

		setCreateInvoiceDialog({
			open: false,
			customer: null,
			request: null,
			estimatedDeliveryDate: "",
		});
		setInvoiceSaveStatus("");
		setInvoiceDialogOpen(true);
	};

	const handleCloseCreateInvoiceDialog = () => {
		setCreateInvoiceDialog({
			open: false,
			customer: null,
			request: null,
			estimatedDeliveryDate: "",
		});
	};

	const handleInvoiceFormChange = (e) => {
		setInvoiceForm({ ...invoiceForm, [e.target.name]: e.target.value });
	};

	const handleInvoiceLineItemChange = (index, field, value) => {
		const updated = [...invoiceForm.line_items];
		updated[index][field] = value;
		setInvoiceForm({
			...invoiceForm,
			line_items: updated,
			total: updated.reduce((sum, item) => sum + item.quantity * item.price, 0),
		});
	};

	const addInvoiceLineItem = () => {
		const updated = [
			...invoiceForm.line_items,
			{ description: "", quantity: 1, price: 0 },
		];
		setInvoiceForm({ ...invoiceForm, line_items: updated });
	};

	const removeInvoiceLineItem = (index) => {
		const updated = invoiceForm.line_items.filter((_, i) => i !== index);
		setInvoiceForm({
			...invoiceForm,
			line_items: updated,
			total: updated.reduce((sum, item) => sum + item.quantity * item.price, 0),
		});
	};

	const handleSaveInvoice = async () => {
		setInvoiceSaveStatus("");
		setInvoiceSaving(true);
		const { error } = await supabase
			.from("invoices")
			.insert([invoiceForm])
			.select("*")
			.single();
		if (error) {
			setInvoiceSaveStatus("error");
			setInvoiceSaving(false);
			return;
		}
		setInvoiceSaveStatus("success");
		setInvoiceSaving(false);
		setInvoiceDialogOpen(false);
		setInvoiceForm(null);
		setInvoiceEstimatedDeliveryDate("");
	};

	// ── Mark as Delivered ────────────────────────────────────────────────────

	const handleOpenMarkAsDeliveredModal = (customer, request) => {
		setMarkAsDeliveredModal({ open: true, customer, request, loading: false });
	};

	const handleCloseMarkAsDeliveredModal = () => {
		setMarkAsDeliveredModal({
			open: false,
			customer: null,
			request: null,
			loading: false,
		});
	};

	const handleMarkAsDelivered = async () => {
		setMarkAsDeliveredModal((prev) => ({ ...prev, loading: true }));
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session) {
			setShow401(true);
			setMarkAsDeliveredModal((prev) => ({ ...prev, loading: false }));
			return;
		}

		const response = await fetch(
			`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/mark-egg-order-delivered`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${session.access_token}`,
				},
				body: JSON.stringify({ request_id: markAsDeliveredModal.request.id }),
			},
		);

		if (response.ok) {
			handleCloseMarkAsDeliveredModal();
			await refreshEggList();
		} else {
			setMarkAsDeliveredModal((prev) => ({ ...prev, loading: false }));
		}
	};

	const handleMarkAsPickedUp = () => {
		const markEggsPickedUp = async () => {
			setStatus("loading");
			const { error } = await supabase
				.from("egg_requests")
				.update({
					request_status: "completed",
					marked_as_picked_up_at: new Date(),
				})
				.eq("id", markAsPickedUpModal.request.id);
			if (error) {
				setError(error.message);
				setStatus("error");
				return;
			}
			// Refresh the list
			const { data: eggData, error: eggError } = await supabase
				.from("egg_list")
				.select("*")
				.order("created_at", { ascending: false });
			if (eggError) {
				setError(eggError.message);
				setStatus("error");
				return;
			}
			const { data: eggRequests, error: eggRequestsError } = await supabase
				.from("egg_requests")
				.select("*")
				.order("requested_at", { ascending: false });
			if (eggRequestsError) {
				setError(eggRequestsError.message);
				setStatus("error");
				return;
			}
			// Attach active request to each customer
			const eggListWithRequests = eggData.map((customer) => {
				const anyRequest = eggRequests.find(
					(request) => request.customer_id === customer.supabaseId,
				);
				return {
					...customer,
					anyRequest,
				};
			});
			setEggList(eggListWithRequests);
			setStatus("loaded");
		};
		markEggsPickedUp();
		setMarkAsPickedUpModal({ open: false, customer: null, request: null });
	};

	if (show401) {
		return <div>You are not authorized to view this page.</div>;
	}

	if (error) {
		return <div>Error: {error}</div>;
	}

	if (status === "loading") {
		return <CircularProgress />;
	}

	const filteredEggList = (
		showActive === "pending"
			? eggList.filter(
					(c) => c.anyRequest && c.anyRequest.request_status === "pending",
				)
			: showActive === "awaiting_pickup"
				? eggList.filter(
						(c) =>
							c.anyRequest &&
							(c.anyRequest.request_status === "awaiting_pickup" ||
								c.anyRequest.request_status === "awaiting_delivery"),
					)
				: showActive === "inactive"
					? eggList.filter(
							(c) =>
								!c.anyRequest ||
								c.anyRequest.request_status === "completed" ||
								c.anyRequest.request_status === "cancelled",
						)
					: showActive === "cancelled"
						? eggList.filter(
								(c) =>
									c.anyRequest && c.anyRequest.request_status === "cancelled",
							)
						: []
	).sort((a, b) => {
		const aDate = a.anyRequest?.requested_at
			? new Date(a.anyRequest.requested_at)
			: new Date(0);
		const bDate = b.anyRequest?.requested_at
			? new Date(b.anyRequest.requested_at)
			: new Date(0);
		return aDate - bDate; // oldest first
	});

	return (
		<Box sx={{ paddingLeft: 2 }}>
			<Box sx={{ mb: 2 }}>
				<ToggleButtonGroup
					value={showActive}
					exclusive
					onChange={handleToggle}
					aria-label="active-inactive-toggle"
					size="small"
				>
					<ToggleButton value="pending">Pending</ToggleButton>
					<ToggleButton value="awaiting_pickup">Open</ToggleButton>
					<ToggleButton value="inactive">Inactive</ToggleButton>
					<ToggleButton value="cancelled">Cancelled</ToggleButton>
				</ToggleButtonGroup>
			</Box>
			<Modal open={delayModal.open} onClose={handleCloseDelayModal}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						minWidth: 320,
					}}
				>
					<Typography variant="h6" mb={2}>
						Send Delay Notification
					</Typography>
					<Typography>
						<b>Name:</b> {delayModal.customer?.first_name}{" "}
						{delayModal.customer?.last_name}
					</Typography>
					<Typography>
						<b>Eggs:</b> Chicken:{" "}
						{formatEggQuantity(delayModal.request?.chicken_eggs_requested || 0)}, Duck:{" "}
						{formatEggQuantity(delayModal.request?.duck_eggs_requested || 0)}
					</Typography>
					<TextField
						label="Delay Note"
						fullWidth
						multiline
						minRows={2}
						value={delayModal.note}
						onChange={(e) =>
							setDelayModal((prev) => ({ ...prev, note: e.target.value }))
						}
						sx={{ mb: 2, mt: 2 }}
					/>
					<Box sx={{ mt: 3, display: "flex", gap: 2 }}>
						<Button
							variant="contained"
							color="warning"
							onClick={handleSendDelay}
							disabled={delayModal.loading}
						>
							{delayModal.loading ? (
								<CircularProgress size={20} />
							) : (
								"Send Notification"
							)}
						</Button>
						<Button variant="outlined" onClick={handleCloseDelayModal}>
							Exit
						</Button>
					</Box>
				</Box>
			</Modal>

			<Modal open={markReadyModal.open} onClose={handleCloseMarkReady}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						minWidth: 320,
					}}
				>
					{markReadyModal.customer && markReadyModal.request && (
						<>
							<Typography variant="h6" mb={2}>
								Mark Eggs Ready
							</Typography>
							<Typography>
								<b>Name:</b> {markReadyModal.customer.first_name}{" "}
								{markReadyModal.customer.last_name}
							</Typography>
							<Typography>
								<b>Chicken eggs:</b>{" "}
								{formatEggQuantity(
									markReadyModal.request.chicken_eggs_requested || 0,
								)}
							</Typography>
							<Typography>
								<b>Duck eggs:</b>{" "}
								{formatEggQuantity(markReadyModal.request.duck_eggs_requested || 0)}
							</Typography>
							<Typography>
								<b>Requested:</b>{" "}
								{new Date(markReadyModal.request.requested_at).toLocaleString()}
							</Typography>
							<TextField
								label="Fulfillment Note (optional)"
								fullWidth
								multiline
								minRows={2}
								value={fulfillmentNote}
								onChange={(e) => setFulfillmentNote(e.target.value)}
								sx={{ mb: 2, mt: 2 }}
							/>
							{markingAsReady ? (
								<CircularProgress sx={{ mt: 2 }} />
							) : (
								<Box sx={{ mt: 3, display: "flex", gap: 2 }}>
									<Button
										variant="contained"
										color="success"
										onClick={() => handleMarkReady(markReadyModal.request.id)}
									>
										Mark Ready
									</Button>
									<Button variant="outlined" onClick={handleCloseMarkReady}>
										Exit
									</Button>
								</Box>
							)}
						</>
					)}
				</Box>
			</Modal>
			<Modal
				open={markAsPickedUpModal.open}
				onClose={handleCloseMarkAsPickedUp}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						minWidth: 320,
					}}
				>
					{markAsPickedUpModal.customer && markAsPickedUpModal.request && (
						<>
							<Typography variant="h6" mb={2}>
								Mark Eggs Picked Up
							</Typography>
							<Typography>
								<b>Name:</b> {markAsPickedUpModal.customer.first_name}{" "}
								{markAsPickedUpModal.customer.last_name}
							</Typography>
							<Typography>
								<b>Chicken eggs:</b>{" "}
								{formatEggQuantity(
									markAsPickedUpModal.request.chicken_eggs_requested || 0,
								)}
							</Typography>
							<Typography>
								<b>Duck eggs:</b>{" "}
								{formatEggQuantity(
									markAsPickedUpModal.request.duck_eggs_requested || 0,
								)}
							</Typography>
							<Typography>
								<b>Requested:</b>{" "}
								{new Date(
									markAsPickedUpModal.request.requested_at,
								).toLocaleString()}
							</Typography>

							<Box sx={{ mt: 3, display: "flex", gap: 2 }}>
								<Button
									variant="contained"
									color="success"
									onClick={handleMarkAsPickedUp}
								>
									Mark Picked Up
								</Button>
								<Button variant="outlined" onClick={handleCloseMarkAsPickedUp}>
									Exit
								</Button>
							</Box>
						</>
					)}
				</Box>
			</Modal>
			<Modal open={cancelModal.open} onClose={handleCloseCancelModal}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						minWidth: 320,
					}}
				>
					<Typography variant="h6" mb={2}>
						Confirm Cancel Order
					</Typography>
					<Typography mb={2}>
						Are you sure you want to cancel this order?
					</Typography>
					<TextField
						label="Cancellation Reason (optional)"
						fullWidth
						multiline
						minRows={2}
						value={cancelModal.reason}
						onChange={(e) =>
							setCancelModal((prev) => ({ ...prev, reason: e.target.value }))
						}
						sx={{ mb: 2 }}
					/>
					<Box sx={{ display: "flex", gap: 2, mt: 2 }}>
						<Button
							variant="contained"
							color="error"
							onClick={handleCancelOrder}
							disabled={cancelModal.loading}
						>
							{cancelModal.loading ? (
								<CircularProgress size={20} />
							) : (
								"Confirm Cancel"
							)}
						</Button>
						<Button variant="outlined" onClick={handleCloseCancelModal}>
							Exit
						</Button>
					</Box>
				</Box>
			</Modal>
			<Modal open={remindModal.open} onClose={handleCloseRemindModal}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						minWidth: 320,
					}}
				>
					<Typography variant="h6" mb={2}>
						Confirm Send Reminder
					</Typography>
					<Typography>
						<b>Requested:</b>{" "}
						{remindModal.request?.requested_at
							? new Date(remindModal.request.requested_at).toLocaleString()
							: "Unknown"}
					</Typography>
					<Typography>
						<b>Fulfilled:</b>{" "}
						{remindModal.request?.fulfilled_at
							? new Date(remindModal.request.fulfilled_at).toLocaleString()
							: "Unknown"}
					</Typography>
					<Box sx={{ mt: 3, display: "flex", gap: 2 }}>
						<Button
							variant="contained"
							color="warning"
							onClick={handleConfirmRemind}
							disabled={remindModal.loading}
						>
							{remindModal.loading ? (
								<CircularProgress size={20} />
							) : (
								"Send Reminder"
							)}
						</Button>
						<Button variant="outlined" onClick={handleCloseRemindModal}>
							Exit
						</Button>
					</Box>
				</Box>
			</Modal>

			<Box mt={2}>
				{filteredEggList.length === 0 ? (
					<li>No egg customers</li>
				) : (
					filteredEggList.map((eggCustomer) => (
						<Box key={eggCustomer.id} mb={2} p={2} border={1} borderRadius={2}>
							<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
								<Typography variant="h6">
									{eggCustomer.first_name} {eggCustomer.last_name}
								</Typography>
								{eggCustomer.elevated_ordering && (
									<Chip
										label="Wholesale Account"
										color="primary"
										size="small"
									/>
								)}
							</Box>
							<Box mt={0.5} mb={1}>
								<Tooltip
									title={
										eggCustomer.elevated_ordering
											? "Revoke elevated ordering (revert to 3 dozen max)"
											: "Grant elevated ordering (allow up to 10 dozen)"
									}
								>
									<Button
										size="small"
										variant={
											eggCustomer.elevated_ordering ? "contained" : "outlined"
										}
										color={eggCustomer.elevated_ordering ? "warning" : "info"}
										onClick={() => handleToggleElevatedOrdering(eggCustomer)}
									>
										{eggCustomer.elevated_ordering
											? "Revoke Wholesale"
											: "Grant Wholesale"}
									</Button>
								</Tooltip>
							</Box>

							{showActive === "inactive" && (
								<Box sx={{ mb: 1 }}>
									<Typography>Address: {eggCustomer.address}</Typography>
									<Typography>
										Email: {eggCustomer.email || " No email found"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Completed Orders: {getUserStats(eggCustomer).completed} |
										Pending Orders: {getUserStats(eggCustomer).pending} |
										Cancelled Orders: {getUserStats(eggCustomer).cancelled}
									</Typography>
								</Box>
							)}
							{eggCustomer.anyRequest &&
								eggCustomer.anyRequest.request_status === "pending" && (
									<Box mt={1}>
										<Typography>Address: {eggCustomer.address}</Typography>
										<Typography>
											Email: {eggCustomer.email || " No email found"}
										</Typography>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography sx={{ color: "blue" }}>
												Requested:{" "}
												{new Date(
													eggCustomer.anyRequest.requested_at,
												).toLocaleString()}
											</Typography>
										</Box>
										{eggCustomer.anyRequest.delay_notification_sent_at && (
											<Box sx={{ display: "flex", gap: 1 }}>
												<Typography sx={{ color: "red" }}>
													Delay Notified at:{" "}
													{new Date(
														eggCustomer.anyRequest.delay_notification_sent_at,
													).toLocaleString()}
												</Typography>
											</Box>
										)}
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography sx={{ color: "orange" }}>
												Status: Pending Fulfillment
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography>
												Chicken Eggs:{" "}
												{formatEggQuantity(
													eggCustomer.anyRequest.chicken_eggs_requested || 0,
												)}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography>
												Ducks Eggs:{" "}
												{formatEggQuantity(
													eggCustomer.anyRequest.duck_eggs_requested || 0,
												)}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography>
												Notes: {eggCustomer.anyRequest.notes || "None"}
											</Typography>
										</Box>
										<Box sx={{ mt: 1, display: "flex", gap: 1 }}>
											<Button
												variant="outlined"
												color="error"
												onClick={() =>
													handleOpenCancelModal(
														eggCustomer,
														eggCustomer.anyRequest,
													)
												}
											>
												Cancel Order
											</Button>
										</Box>
										{eggCustomer.anyRequest.fulfillment_type !== "delivery" && (
											<Box sx={{ mt: 1, display: "flex", gap: 1 }}>
												<Button
													variant="contained"
													color="success"
													onClick={() =>
														handleOpenMarkReady(
															eggCustomer,
															eggCustomer.anyRequest,
														)
													}
												>
													Mark Eggs Ready
												</Button>
											</Box>
										)}
										{eggCustomer.elevated_ordering && (
											<Box sx={{ mt: 1, display: "flex", gap: 1 }}>
												<Button
													variant="contained"
													color="primary"
													onClick={() =>
														handleOpenAcknowledgeModal(
															eggCustomer,
															eggCustomer.anyRequest,
														)
													}
												>
													Acknowledge Wholesale Order
												</Button>
											</Box>
										)}
										<Box sx={{ mt: 1, display: "flex", gap: 1 }}>
											<Button
												variant="outlined"
												color="warning"
												disabled={
													eggCustomer.anyRequest.delay_notification_sent
												}
												onClick={() =>
													handleOpenDelayModal(
														eggCustomer,
														eggCustomer.anyRequest,
													)
												}
											>
												{eggCustomer.anyRequest.delay_notification_sent
													? "Delay Notified"
													: "Send Delay Notification"}
											</Button>
										</Box>
									</Box>
								)}
							{eggCustomer.anyRequest &&
								eggCustomer.anyRequest.request_status === "awaiting_pickup" && (
									<Box mt={1}>
										<Typography>Address: {eggCustomer.address}</Typography>
										<Typography>
											Email: {eggCustomer.email || " No email found"}
										</Typography>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography sx={{ color: "blue" }}>
												Requested at:{" "}
												{new Date(
													eggCustomer.anyRequest.requested_at,
												).toLocaleString()}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography sx={{ color: "green" }}>
												Fulfilled at:{" "}
												{new Date(
													eggCustomer.anyRequest.fulfilled_at,
												).toLocaleString()}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography sx={{ color: "orange" }}>
												Status: Awaiting Pickup
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography>
												Chicken Eggs:{" "}
												{formatEggQuantity(
													eggCustomer.anyRequest.chicken_eggs_requested || 0,
												)}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography>
												Ducks Eggs:{" "}
												{formatEggQuantity(
													eggCustomer.anyRequest.duck_eggs_requested || 0,
												)}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography>
												Notes: {eggCustomer.anyRequest.notes || "None"}
											</Typography>
										</Box>
										<Box sx={{ mt: 1, display: "flex", gap: 1 }}>
											<Button
												variant="contained"
												color="success"
												onClick={() =>
													handleOpenMarkAsPickedUp(
														eggCustomer,
														eggCustomer.anyRequest,
													)
												}
											>
												Mark Eggs Picked Up
											</Button>
										</Box>
										<Box sx={{ mt: 1, display: "flex", gap: 1 }}>
											<Button
												variant="outlined"
												color="error"
												onClick={() =>
													handleOpenCancelModal(
														eggCustomer,
														eggCustomer.anyRequest,
													)
												}
											>
												Cancel Order
											</Button>
										</Box>

										<Box sx={{ mt: 1 }}>
											<Tooltip
												title={
													eggCustomer.anyRequest.has_been_reminded
														? "A reminder was already sent to the user"
														: ""
												}
												disableHoverListener={
													!eggCustomer.anyRequest.has_been_reminded
												}
											>
												<span>
													<Button
														variant="outlined"
														color="warning"
														disabled={eggCustomer.anyRequest.has_been_reminded}
														onClick={() =>
															handleOpenRemindModal(
																eggCustomer,
																eggCustomer.anyRequest,
															)
														}
													>
														{eggCustomer.anyRequest.has_been_reminded
															? "Reminder Sent"
															: "Send Pickup Reminder"}
													</Button>
												</span>
											</Tooltip>
										</Box>
									</Box>
								)}
							{eggCustomer.anyRequest &&
								eggCustomer.anyRequest.request_status ===
									"awaiting_delivery" && (
									<Box mt={1}>
										<Typography>Address: {eggCustomer.address}</Typography>
										<Typography>
											Email: {eggCustomer.email || " No email found"}
										</Typography>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography sx={{ color: "blue" }}>
												Requested at:{" "}
												{new Date(
													eggCustomer.anyRequest.requested_at,
												).toLocaleString()}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography sx={{ color: "green" }}>
												Acknowledged at:{" "}
												{eggCustomer.anyRequest.acknowledged_at
													? new Date(
															eggCustomer.anyRequest.acknowledged_at,
														).toLocaleString()
													: "Unknown"}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography sx={{ color: "purple" }}>
												Estimated Delivery:{" "}
												{eggCustomer.anyRequest.estimated_delivery_date
													? new Date(
															eggCustomer.anyRequest.estimated_delivery_date +
																"T00:00:00",
														).toLocaleDateString()
													: "Not set"}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography sx={{ color: "orange" }}>
												Status: Awaiting Delivery
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography>
												Chicken Eggs:{" "}
												{formatEggQuantity(
													eggCustomer.anyRequest.chicken_eggs_requested || 0,
												)}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography>
												Duck Eggs:{" "}
												{formatEggQuantity(
													eggCustomer.anyRequest.duck_eggs_requested || 0,
												)}
											</Typography>
										</Box>
										<Box sx={{ display: "flex", gap: 1 }}>
											<Typography>
												Notes: {eggCustomer.anyRequest.notes || "None"}
											</Typography>
										</Box>
										<Box sx={{ mt: 1, display: "flex", gap: 1 }}>
											<Button
												variant="contained"
												color="success"
												onClick={() =>
													handleOpenMarkAsDeliveredModal(
														eggCustomer,
														eggCustomer.anyRequest,
													)
												}
											>
												Mark as Delivered
											</Button>
										</Box>
										<Box sx={{ mt: 1, display: "flex", gap: 1 }}>
											<Button
												variant="outlined"
												color="error"
												onClick={() =>
													handleOpenCancelModal(
														eggCustomer,
														eggCustomer.anyRequest,
													)
												}
											>
												Cancel Order
											</Button>
										</Box>
									</Box>
								)}
							{showActive === "cancelled" &&
								eggCustomer.anyRequest &&
								eggCustomer.anyRequest.request_status === "cancelled" && (
									<Box mt={1}>
										<Typography sx={{ color: "red" }}>
											Order Cancelled
										</Typography>
										<Typography>
											Cancellation Reason:{" "}
											{eggCustomer.anyRequest.cancellation_reason || "None"}
										</Typography>
										<Typography>
											Requested:{" "}
											{eggCustomer.anyRequest.requested_at
												? new Date(
														eggCustomer.anyRequest.requested_at,
													).toLocaleString()
												: "Unknown"}
										</Typography>
										<Typography>
											Date Cancelled:{" "}
											{eggCustomer.anyRequest.cancelled_at
												? new Date(
														eggCustomer.anyRequest.cancelled_at,
													).toLocaleString()
												: "Unknown"}
										</Typography>
									</Box>
								)}
						</Box>
					))
				)}
			</Box>

			{/* Acknowledge Wholesale Order Modal */}
			<Modal open={acknowledgeModal.open} onClose={handleCloseAcknowledgeModal}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						minWidth: 340,
						maxWidth: 480,
						width: "90%",
					}}
				>
					{acknowledgeModal.customer && acknowledgeModal.request && (
						<>
							<Typography variant="h6" mb={2}>
								Acknowledge Wholesale Order
							</Typography>
							<Typography>
								<b>Customer:</b> {acknowledgeModal.customer.first_name}{" "}
								{acknowledgeModal.customer.last_name}
							</Typography>
							<Typography>
								<b>Chicken Eggs:</b>{" "}
								{formatEggQuantity(
									acknowledgeModal.request.chicken_eggs_requested || 0,
								)}
							</Typography>
							<Typography>
								<b>Duck Eggs:</b>{" "}
								{formatEggQuantity(acknowledgeModal.request.duck_eggs_requested || 0)}
							</Typography>
							<TextField
								label="Estimated Delivery Date"
								type="date"
								fullWidth
								value={acknowledgeModal.estimatedDeliveryDate}
								onChange={(e) =>
									setAcknowledgeModal((prev) => ({
										...prev,
										estimatedDeliveryDate: e.target.value,
									}))
								}
								InputLabelProps={{ shrink: true }}
								sx={{ mt: 2, mb: 1 }}
								required
							/>
							<TextField
								label="Fulfillment Note (optional)"
								fullWidth
								multiline
								minRows={2}
								value={acknowledgeModal.fulfillmentNote}
								onChange={(e) =>
									setAcknowledgeModal((prev) => ({
										...prev,
										fulfillmentNote: e.target.value,
									}))
								}
								sx={{ mb: 2 }}
							/>
							<Box sx={{ display: "flex", gap: 2 }}>
								<Button
									variant="contained"
									color="primary"
									onClick={handleAcknowledgeWholesaleOrder}
									disabled={
										acknowledgeModal.loading ||
										!acknowledgeModal.estimatedDeliveryDate
									}
								>
									{acknowledgeModal.loading ? (
										<CircularProgress size={20} />
									) : (
										"Acknowledge & Notify"
									)}
								</Button>
								<Button
									variant="outlined"
									onClick={handleCloseAcknowledgeModal}
								>
									Exit
								</Button>
							</Box>
						</>
					)}
				</Box>
			</Modal>

			{/* Create Invoice After Acknowledge Dialog */}
			<Dialog
				open={createInvoiceDialog.open}
				onClose={handleCloseCreateInvoiceDialog}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Create Invoice?</DialogTitle>
				<DialogContent>
					<Typography>
						The order for{" "}
						<b>
							{createInvoiceDialog.customer?.first_name}{" "}
							{createInvoiceDialog.customer?.last_name}
						</b>{" "}
						has been acknowledged. Would you like to create an invoice for this
						order?
					</Typography>
					{createInvoiceDialog.estimatedDeliveryDate && (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							Estimated delivery:{" "}
							{new Date(
								createInvoiceDialog.estimatedDeliveryDate + "T00:00:00",
							).toLocaleDateString()}
							. Invoice due date will be set {WHOLESALE_PAYMENT_TERMS_DAYS} days
							after delivery.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseCreateInvoiceDialog}>Not Now</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOpenInvoiceFromOrder}
					>
						Create Invoice
					</Button>
				</DialogActions>
			</Dialog>

			{/* Pre-populated Invoice Creation Dialog */}
			<Dialog
				open={invoiceDialogOpen}
				onClose={() => {
					setInvoiceDialogOpen(false);
					setInvoiceEstimatedDeliveryDate("");
				}}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Create Invoice</DialogTitle>
				<DialogContent sx={{ p: 1 }}>
					{invoiceForm && (
						<>
							<TextField
								label="Customer Name"
								name="customer_name"
								value={invoiceForm.customer_name}
								onChange={handleInvoiceFormChange}
								fullWidth
								margin="normal"
								size="small"
							/>
							<TextField
								label="Customer Email"
								name="customer_email"
								value={invoiceForm.customer_email}
								onChange={handleInvoiceFormChange}
								fullWidth
								margin="normal"
								size="small"
							/>
							<TextField
								label="Phone Number"
								name="phone_number"
								value={invoiceForm.phone_number}
								onChange={handleInvoiceFormChange}
								fullWidth
								margin="normal"
								size="small"
							/>
							{invoiceEstimatedDeliveryDate && (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mt: 1 }}
								>
									Estimated delivery date:{" "}
									{new Date(
										invoiceEstimatedDeliveryDate + "T00:00:00",
									).toLocaleDateString()}
								</Typography>
							)}
							<TextField
								label={`Due Date (${WHOLESALE_PAYMENT_TERMS_DAYS} days after delivery)`}
								name="due_date"
								value={invoiceForm.due_date}
								onChange={handleInvoiceFormChange}
								type="date"
								fullWidth
								margin="normal"
								InputLabelProps={{ shrink: true }}
								size="small"
							/>
							<Typography variant="subtitle1" sx={{ mt: 2 }}>
								Line Items
							</Typography>
							{invoiceForm.line_items.map((item, index) => (
								<Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										label="Description"
										value={item.description}
										onChange={(e) =>
											handleInvoiceLineItemChange(
												index,
												"description",
												e.target.value,
											)
										}
										fullWidth
										size="small"
									/>
									<TextField
										label="Qty"
										type="number"
										value={item.quantity}
										onChange={(e) =>
											handleInvoiceLineItemChange(
												index,
												"quantity",
												Number(e.target.value),
											)
										}
										size="small"
										sx={{ width: 80 }}
									/>
									<TextField
										label="Price"
										type="number"
										value={item.price}
										onChange={(e) =>
											handleInvoiceLineItemChange(
												index,
												"price",
												Number(e.target.value),
											)
										}
										size="small"
										sx={{ width: 90 }}
									/>
									<Button
										size="small"
										color="error"
										onClick={() => removeInvoiceLineItem(index)}
									>
										Remove
									</Button>
								</Box>
							))}
							<Button size="small" onClick={addInvoiceLineItem} sx={{ mt: 1 }}>
								Add Line Item
							</Button>
							<Typography variant="subtitle1" sx={{ mt: 2 }}>
								Total: ${invoiceForm.total.toFixed(2)}
							</Typography>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setInvoiceDialogOpen(false);
							setInvoiceEstimatedDeliveryDate("");
						}}
						disabled={invoiceSaving}
					>
						Cancel
					</Button>
					<Button
						onClick={handleSaveInvoice}
						variant="contained"
						color="primary"
						disabled={invoiceSaving}
					>
						{invoiceSaving ? <CircularProgress size={20} /> : "Save Invoice"}
					</Button>
				</DialogActions>
				{invoiceSaveStatus === "error" && (
					<Box p={2} color="error.main">
						Error saving invoice. Please try again.
					</Box>
				)}
			</Dialog>

			{/* Mark as Delivered Modal */}
			<Modal
				open={markAsDeliveredModal.open}
				onClose={handleCloseMarkAsDeliveredModal}
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
						borderRadius: 2,
						minWidth: 320,
					}}
				>
					{markAsDeliveredModal.customer && markAsDeliveredModal.request && (
						<>
							<Typography variant="h6" mb={2}>
								Mark Order as Delivered
							</Typography>
							<Typography>
								<b>Customer:</b> {markAsDeliveredModal.customer.first_name}{" "}
								{markAsDeliveredModal.customer.last_name}
							</Typography>
							<Typography>
								<b>Chicken Eggs:</b>{" "}
								{formatEggQuantity(
									markAsDeliveredModal.request.chicken_eggs_requested || 0,
								)}
							</Typography>
							<Typography>
								<b>Duck Eggs:</b>{" "}
								{formatEggQuantity(
									markAsDeliveredModal.request.duck_eggs_requested || 0,
								)}
							</Typography>
							{markAsDeliveredModal.request.estimated_delivery_date && (
								<Typography>
									<b>Estimated Delivery:</b>{" "}
									{new Date(
										markAsDeliveredModal.request.estimated_delivery_date +
											"T00:00:00",
									).toLocaleDateString()}
								</Typography>
							)}
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								This will mark the order as completed and record the delivery
								date.
							</Typography>
							<Box sx={{ mt: 3, display: "flex", gap: 2 }}>
								<Button
									variant="contained"
									color="success"
									onClick={handleMarkAsDelivered}
									disabled={markAsDeliveredModal.loading}
								>
									{markAsDeliveredModal.loading ? (
										<CircularProgress size={20} />
									) : (
										"Confirm Delivered"
									)}
								</Button>
								<Button
									variant="outlined"
									onClick={handleCloseMarkAsDeliveredModal}
								>
									Exit
								</Button>
							</Box>
						</>
					)}
				</Box>
			</Modal>
		</Box>
	);
};

export default AdminEggList;
